import queryString from 'query-string';

import { Loading } from '#components/loading.js';
import { RouterErrorBoundary } from '#screens/error-boundaries/router-error-boundary.js';
import { NotFound } from '#screens/not-found.js';
import { Auth0ScreenHint } from '#utils/auth0-screen-hint.js';
import { QUERY_RETRY_DELAY_MS, shouldRetryQuery } from '#utils/query-retry.js';

import type { Auth0Client } from '@auth0/auth0-spa-js';
import type { QueryClientConfig } from '@tanstack/react-query';
import type { createRouter } from '@tanstack/react-router';
import type { ParseOptions, StringifyOptions } from 'query-string';

export function doRedirects() {
	const pathname = window.location.pathname.replace(/\/$/, '');
	const replacePathname = (newPath: string) => {
		window.history.replaceState(null, '', newPath + window.location.search + window.location.hash);
	};

	// These routes log in/out the user. Their canonical forms are `/log-in` and `/log-out` (because they're verbs),
	// but we also accept `/login` and `/logout` for convenience.
	if (pathname === '/login') {
		replacePathname('/log-in');
		return;
	} else if (pathname === '/logout') {
		replacePathname('/log-out');
		return;
	}

	// `/sign-up` (and `/signup`) is the shareable entry point for self-serve signup. It lands on the login route
	// carrying the hint that opens Auth0's widget on its signup tab rather than its login tab.
	if (pathname === '/sign-up' || pathname === '/signup') {
		const search = new URLSearchParams(window.location.search);
		search.set('screen_hint', Auth0ScreenHint.Signup);
		window.history.replaceState(null, '', `/log-in?${search.toString()}${window.location.hash}`);
		return;
	}

	// 2026-02-20 backwards compatibility redirects, see PR description: https://dev.azure.com/cadstrom/Cadstrom/_git/monorepo/pullrequest/2363
	const backwardsRoutes = /^\/(?<page>ai-findings|resolution-editor|results)\/(?<projRev>.+?\/revisions\/.+?)$/.exec(
		pathname,
	);
	if (backwardsRoutes !== null && backwardsRoutes.groups) {
		replacePathname(`/projects/${backwardsRoutes.groups['projRev']}/${backwardsRoutes.groups['page']}`);
		return;
	}
}

const queryStringOptions = {
	arrayFormat: 'bracket-separator',
} satisfies StringifyOptions & ParseOptions;

export const tanstackRouterOptions = {
	defaultStructuralSharing: true,
	defaultErrorComponent: RouterErrorBoundary,
	defaultNotFoundComponent: NotFound,
	defaultPendingComponent: Loading,
	// Show pending UI immediately instead of a blank screen while route loaders are blocking.
	defaultPendingMs: 0,
	defaultPendingMinMs: 200,
	// Turn off TanStack Router's route loader caching in favour of TanStack Query's query cache: https://tanstack.com/router/latest/docs/framework/react/guide/data-loading#passing-all-loader-events-to-an-external-cache
	defaultPreloadStaleTime: 0,
	stringifySearch: (value: Record<string, unknown>) => {
		const str = queryString.stringify(value, queryStringOptions);
		return str === '' ? '' : `?${str}`;
	},
	parseSearch: (value: string) => queryString.parse(value, queryStringOptions),
} as const satisfies Parameters<typeof createRouter>['0'];

export function getTanstackQueryOptions(auth0Client: Auth0Client) {
	return {
		defaultOptions: {
			queries: {
				staleTime: 2 * 60 * 1000, // 2 minutes
				retry: shouldRetryQuery,
				retryDelay: QUERY_RETRY_DELAY_MS,
				throwOnError: true,
				meta: {
					auth0Client,
				},
			},
			mutations: {
				meta: {
					auth0Client,
				},
			},
		},
	} satisfies QueryClientConfig;
}
